import React, { useEffect, useRef, useState } from 'react';
import { css } from '@emotion/core';
import styled from '@emotion/styled';
import { Box, Icon } from '@qga/roo-ui/components';
import PropTypes from 'prop-types';

import LinkButton from '@/components/LinkButton';
import Text from '@/components/Text';
import useSize from '@/shared/hooks/useSize';
import isInViewPort from '@/shared/utils/isInViewPort';

const InvisibleBox = styled(Box)`
  visibility: hidden;
  position: absolute;
  top: 0px;
  opacity: 0;
`;

const Truncate = styled(Box)`
  overflow: hidden;

  ${(props) =>
    props.truncated &&
    css`
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: ${props.lines < 1 ? 'unset' : props.lines};
    `}
`;

/**
 * @typedef {Object} ReadMoreProps
 * @property {import('react').ReactNode} children
 * @property {string} [showMoreText]
 * @property {string} [showLessText]
 * @property {boolean} [displayIcons]
 * @property {number} [lines]
 * @property {import('@qga/roo-ui/components').BoxProps['maxWidth']} [maxWidth]
 * @property {boolean} [scrollBack]
 * @property {number} [lineHeight]
 */

/**
 * @param {ReadMoreProps} props
 */
const ReadMore = ({
  lines = 5,
  children,
  showMoreText = 'Read more',
  showLessText = 'Read less',
  displayIcons = false,
  maxWidth,
  scrollBack = true,
  lineHeight = 32,
}) => {
  const ref = useRef();
  const [expanded, setExpanded] = useState(false);
  const [showButton, setShowButton] = useState(true);
  const {
    height: heightContainer,
    width: widthContainer,
    ref: refContainer,
  } = useSize();
  const { height: heightContent, ref: refContent } = useSize();

  const maxHeight = lines * lineHeight;

  const onClick = (event) => {
    event.preventDefault();
    if (scrollBack && expanded && !isInViewPort(ref.current)) {
      window.scrollTo({
        top: window.pageYOffset + parseFloat(maxHeight - heightContent),
        behavior: 'auto',
      });
    }
    setExpanded(!expanded);
  };

  useEffect(() => {
    if (expanded === false) setShowButton(heightContainer < heightContent);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [heightContent, heightContainer]);

  return (
    <>
      <Box position="relative" maxWidth={maxWidth} ref={ref}>
        <Truncate
          data-testid="NO_OVERFLOW"
          id="read_more"
          truncated={!expanded}
          mb={lines === 0 && !expanded ? 0 : 4}
          lines={lines}
          maxHeight={!expanded && `${maxHeight}px`}
          ref={refContainer}
        >
          {children}
        </Truncate>
        <InvisibleBox ref={refContent} width={`${widthContainer}px`}>
          {children}
        </InvisibleBox>
        {showButton && (
          <LinkButton noPadding onClick={onClick}>
            <Text bold link>
              {expanded ? showLessText : showMoreText}
            </Text>
            {displayIcons && (
              <Icon name={expanded ? 'expandLess' : 'expandMore'} />
            )}
          </LinkButton>
        )}
      </Box>
    </>
  );
};
ReadMore.propTypes = {
  children: PropTypes.node.isRequired,
  showMoreText: PropTypes.string,
  showLessText: PropTypes.string,
  displayIcons: PropTypes.bool,
  lines: PropTypes.number,
  maxWidth: Box.propTypes.maxWidth,
  scrollBack: PropTypes.bool,
  lineHeight: PropTypes.number,
};

export default ReadMore;
